import type { BrandfetchResponse } from "../modules/brand/types";
import type { FontFallbackMap } from "../modules/brand/utils/resolve-brand-fonts";
import { withDesignSystemDefaults } from "@/shared";
import { create } from "zustand";

type PreviewTab = "story" | "sample-slides";

/**
 * Surface the dialog was opened from.
 * - "story": editor — reads/writes the current story's design system slice.
 * - "account": account page — edits a Mongo theme record directly via
 *   `useUpdateThemeMutation`. No story slice involvement; some UI bits
 *   (custom colors, save-as-new, preview tabs) are hidden.
 */
type DialogMode = "story" | "account";

interface IState {
  designSystem: Record<string, unknown>;
  /**
   * Design-system keys the user has edited since the dialog opened.
   *
   * `designSystem` is seeded from the story's full persisted snapshot, so
   * "is this key present" says nothing about whether it was touched. Save
   * needs the difference: `buildAllEntities` writes its recipe onto every
   * matching element in the story, so without this a shape change also
   * rewrote each image's shadow, overlay and corner radius. See
   * build-design-entities.ts.
   */
  dirtyKeys: Set<string>;
  mode: DialogMode;
  /**
   * Staged custom colors. Mirrors `story.settings.customColors` at dialog
   * open and accumulates edits locally so "Save Changes" can dispatch them
   * in one shot, same pattern as `designSystem`.
   */
  customColors: string[];
  isInitialized: boolean;
  isDirty: boolean;
  brandData: BrandfetchResponse | null;
  brandSearchTerm: string;
  previewTab: PreviewTab;
  /** Pending logo URL chosen from the brand-import result card. Persisted on Save. */
  selectedLogoUrl: string | null;
  /**
   * Maps original Brandfetch logo URLs to the Storydoc media-library URLs
   * they were re-uploaded as during brand import. Used so the result-card
   * tiles (which display the original URLs) can hand the corresponding
   * internal URL to `setSelectedLogoUrl` when the user clicks one.
   */
  brandLogoUrlMap: Record<string, string>;
  /**
   * Original Brandfetch URLs whose re-upload to the media library is still
   * in-flight. The result-card tiles read this to show a spinner overlay
   * while a logo is being rehosted.
   */
  uploadingLogoUrls: string[];
  /**
   * Font keys whose current value is a Google Fonts fallback that the AI
   * matcher picked because the brand's actual font (e.g. "Netflix Sans") isn't
   * available. Used to surface a tooltip in the theme editor. Cleared on
   * reset; populated by useBrandImport after a successful resolve.
   */
  fontFallbacks: FontFallbackMap;
}

interface IActions {
  initialize: (
    snapshot: Record<string, unknown>,
    brand?: BrandfetchResponse | null,
    customColors?: string[],
    mode?: DialogMode,
  ) => void;
  update: (partial: Record<string, unknown>) => void;
  updateCustomColors: (colors: string[]) => void;
  setBrandData: (data: BrandfetchResponse | null) => void;
  setBrandSearchTerm: (term: string) => void;
  setPreviewTab: (tab: PreviewTab) => void;
  setSelectedLogoUrl: (url: string | null) => void;
  setBrandLogoUrlMap: (map: Record<string, string>) => void;
  setUploadingLogoUrls: (urls: string[]) => void;
  completeLogoUpload: (originalUrl: string, internalUrl?: string) => void;
  setFontFallbacks: (fallbacks: FontFallbackMap) => void;
  /** Flip isDirty back to false after a successful save. */
  markClean: () => void;
  reset: () => void;
}

export const useDesignSystemStore = create<IState & IActions>((set) => ({
  designSystem: {},
  dirtyKeys: new Set<string>(),
  mode: "story" as DialogMode,
  customColors: [],
  isInitialized: false,
  isDirty: false,
  brandData: null,
  brandSearchTerm: "",
  previewTab: "story" as PreviewTab,
  selectedLogoUrl: null,
  brandLogoUrlMap: {},
  uploadingLogoUrls: [],
  fontFallbacks: {},

  initialize: (snapshot, brand, customColors, mode) => {
    // Backfill display/heading defaults so the dialog never sees undefined
    // for keys that were added after the original designSystemV2 shape was
    // frozen. See shared/utils/design-system-defaults.ts.
    set({
      designSystem: withDesignSystemDefaults({ ...snapshot }),
      // Seeding is not editing: the snapshot (and the backfilled defaults)
      // must not count as user changes.
      dirtyKeys: new Set<string>(),
      mode: mode ?? "story",
      customColors: customColors ?? [],
      isInitialized: true,
      // Rehydrate the previously imported brand snapshot (if any) so the
      // user reopens the dialog into the result-card view instead of the
      // search hero state.
      brandData: brand ?? null,
      // Mirror the brand name into the search input so the post-import
      // result card opens with the correct value pre-filled instead of an
      // empty field. Falls back to the domain if `name` is missing.
      brandSearchTerm: brand ? brand.name || brand.domain || "" : "",
    });
  },

  update: (partial) => {
    set((state) => {
      // If the user (or any caller) is overriding a font key that we
      // previously marked as a fallback, drop the fallback record so the
      // "we substituted your brand font" tooltip stops showing.
      let nextFallbacks = state.fontFallbacks;
      const fallbackKeys = Object.keys(state.fontFallbacks);
      if (fallbackKeys.length > 0) {
        const overridden = fallbackKeys.filter((k) => k in partial);
        if (overridden.length > 0) {
          nextFallbacks = { ...state.fontFallbacks };
          for (const k of overridden) {
            delete (nextFallbacks as Record<string, unknown>)[k];
          }
        }
      }
      const nextDirtyKeys = new Set(state.dirtyKeys);
      for (const key of Object.keys(partial)) nextDirtyKeys.add(key);

      return {
        designSystem: { ...state.designSystem, ...partial },
        dirtyKeys: nextDirtyKeys,
        isDirty: true,
        fontFallbacks: nextFallbacks,
      };
    });
  },

  updateCustomColors: (colors) =>
    set({ customColors: colors, isDirty: true }),

  setBrandData: (data) => set({ brandData: data }),

  setBrandSearchTerm: (term) => set({ brandSearchTerm: term }),

  setPreviewTab: (tab) => set({ previewTab: tab }),

  setSelectedLogoUrl: (url) =>
    // Flip isDirty so the design-system dialog's Save Changes button enables
    // when a logo is the only thing the user changed.
    set({ selectedLogoUrl: url, isDirty: true }),

  setBrandLogoUrlMap: (map) => set({ brandLogoUrlMap: map }),

  setUploadingLogoUrls: (urls) => set({ uploadingLogoUrls: urls }),

  completeLogoUpload: (originalUrl, internalUrl) =>
    set((state) => ({
      uploadingLogoUrls: state.uploadingLogoUrls.filter(
        (u) => u !== originalUrl,
      ),
      brandLogoUrlMap: internalUrl
        ? { ...state.brandLogoUrlMap, [originalUrl]: internalUrl }
        : state.brandLogoUrlMap,
    })),

  setFontFallbacks: (fallbacks) => set({ fontFallbacks: fallbacks }),

  // Called after a successful save, once the entity recipe has been applied —
  // so the next save starts from a clean slate and does not re-apply it.
  markClean: () => set({ isDirty: false, dirtyKeys: new Set<string>() }),

  reset: () =>
    set({
      designSystem: {},
      dirtyKeys: new Set<string>(),
      mode: "story",
      customColors: [],
      isInitialized: false,
      isDirty: false,
      brandData: null,
      brandSearchTerm: "",
      previewTab: "story",
      selectedLogoUrl: null,
      brandLogoUrlMap: {},
      uploadingLogoUrls: [],
      fontFallbacks: {},
    }),
}));

export const useDesignSystemValue = (key: string) =>
  useDesignSystemStore((state) => state.designSystem[key]);

export const useDesignSystemUpdate = () =>
  useDesignSystemStore((state) => state.update);
